import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FornecedoresService } from '../fornecedores.service';
import { NotificationService } from '../../../core/notification.service';

@Component({
  standalone: false,
  selector: 'app-fornecedor-admin-detail',
  templateUrl: './fornecedor-admin-detail.component.html'
})
export class FornecedorAdminDetailComponent implements OnInit {
  fornecedorId = '';
  fornecedor: any = null;
  loading = true;
  saving = false;
  resending = false;

  // Leads
  leads: any[] = [];
  leadsPage = 1;
  leadsPageSize = 10;
  leadsTotal = 0;
  leadsLoading = false;

  // Upgrade modal
  showUpgradeModal = false;
  upgradeForm!: FormGroup;
  upgrading = false;

  // Downgrade modal
  showDowngradeModal = false;
  downgradeForm!: FormGroup;
  downgrading = false;

  // Manual subscription edit modal
  showSubscriptionModal = false;
  subscriptionForm!: FormGroup;
  savingSubscription = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private svc: FornecedoresService,
    private notify: NotificationService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.fornecedorId = this.route.snapshot.params['id'];
    this.load();
    this.buildForms();
  }

  buildForms() {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    this.upgradeForm = this.fb.group({
      reason: ['marketing', Validators.required],
      discount: [0, [Validators.min(0), Validators.max(100)]],
      expiresAt: [oneYearFromNow.toISOString().slice(0, 10), Validators.required],
      notifySupplier: [true]
    });

    this.downgradeForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notifySupplier: [true]
    });

    this.subscriptionForm = this.fb.group({
      planLevel: ['Free', Validators.required],
      paymentStatus: ['active', Validators.required],
      expiresAt: [''],
      vitrineSinceDate: [''],
      valorMensal: [null, [Validators.min(0)]],
      reason: ['Ajuste manual via admin']
    });
  }

  load() {
    this.loading = true;
    this.svc.adminGet(this.fornecedorId).subscribe({
      next: (res: any) => {
        this.fornecedor = res?.data ?? res;
        this.loading = false;
        this.loadLeads();
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao carregar', err);
        this.notify.showError('Erro ao carregar fornecedor');
        this.loading = false;
      }
    });
  }

  loadLeads() {
    if (!this.fornecedorId) return;
    this.leadsLoading = true;
    this.svc.getLeads(this.fornecedorId, { page: this.leadsPage, pageSize: this.leadsPageSize }).subscribe({
      next: (res: any) => {
        this.leads = res?.data ?? (Array.isArray(res) ? res : []);
        this.leadsTotal = res?.meta?.total ?? this.leads.length;
        this.leadsLoading = false;
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao carregar leads', err);
        this.leadsLoading = false;
      }
    });
  }

  toggleAtivo() {
    if (this.saving) return;
    this.saving = true;
    this.svc.toggleActive(this.fornecedorId).subscribe({
      next: () => {
        if (this.fornecedor) {
          this.fornecedor.ativo = !this.fornecedor.ativo;
          this.fornecedor.status = this.fornecedor.ativo ? 'Ativo' : 'Inativo';
        }
        this.saving = false;
        this.notify.showSuccess(this.fornecedor?.ativo ? 'Fornecedor ativado' : 'Fornecedor desativado');
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao toggle ativo', err);
        this.notify.showError('Erro ao alterar status');
        this.saving = false;
      }
    });
  }

  togglePublicado() {
    if (this.saving || !this.fornecedor) return;
    this.saving = true;
    const novoPublicado = !this.fornecedor.publicado;
    this.svc.update(this.fornecedorId, { publicado: novoPublicado }).subscribe({
      next: () => {
        if (this.fornecedor) this.fornecedor.publicado = novoPublicado;
        this.saving = false;
        this.notify.showSuccess(novoPublicado ? 'Fornecedor publicado' : 'Fornecedor despublicado');
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao toggle publicado', err);
        this.notify.showError('Erro ao alterar publicação');
        this.saving = false;
      }
    });
  }

  resendWelcomeEmail() {
    this.resending = true;
    this.svc.resendWelcomeEmail(this.fornecedorId).subscribe({
      next: () => {
        this.notify.showSuccess('Email de boas-vindas reenviado com sucesso');
        this.resending = false;
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao reenviar email', err);
        this.notify.showError('Erro ao reenviar email');
        this.resending = false;
      }
    });
  }

  resetLeads() {
    if (!confirm('Resetar o contador de leads deste fornecedor?')) return;
    this.svc.resetLeads(this.fornecedorId).subscribe({
      next: () => {
        this.notify.showSuccess('Leads resetados com sucesso');
        this.load();
      },
      error: (err) => {
        console.error('[FornecedorAdminDetail] erro ao resetar leads', err);
        this.notify.showError('Erro ao resetar leads');
      }
    });
  }

  // Upgrade
  openUpgradeModal() {
    this.showUpgradeModal = true;
  }

  closeUpgradeModal() {
    this.showUpgradeModal = false;
    this.upgradeForm.reset({
      reason: 'marketing',
      discount: 0,
      notifySupplier: true
    });
  }

  submitUpgrade() {
    if (this.upgradeForm.invalid || this.upgrading) return;
    this.upgrading = true;
    const v = this.upgradeForm.value;
    const payload = {
      planLevel: 'Vitrine',
      expiryDate: v.expiresAt ? new Date(`${v.expiresAt}T00:00:00`).toISOString() : undefined,
      reason: v.reason,
      discount: v.discount ? Number(v.discount) : 0,
      notifySupplier: v.notifySupplier
    };
    this.svc.overridePlan(this.fornecedorId, payload).subscribe({
      next: () => {
        this.upgrading = false;
        this.closeUpgradeModal();
        this.notify.showSuccess('Upgrade para Vitrine realizado com sucesso!');
        this.load();
      },
      error: (err) => {
        this.upgrading = false;
        console.error('[FornecedorAdminDetail] erro ao fazer upgrade', err);
        this.notify.showError('Erro ao fazer upgrade');
      }
    });
  }

  // Downgrade
  openDowngradeModal() {
    this.showDowngradeModal = true;
  }

  closeDowngradeModal() {
    this.showDowngradeModal = false;
    this.downgradeForm.reset({ notifySupplier: true });
  }

  submitDowngrade() {
    if (this.downgradeForm.invalid || this.downgrading) return;
    this.downgrading = true;
    const v = this.downgradeForm.value;
    const payload = { planLevel: 'Free', reason: v.reason, notifySupplier: v.notifySupplier };
    this.svc.overridePlan(this.fornecedorId, payload).subscribe({
      next: () => {
        this.downgrading = false;
        this.closeDowngradeModal();
        this.notify.showSuccess('Downgrade para Free realizado com sucesso');
        this.load();
      },
      error: (err) => {
        this.downgrading = false;
        console.error('[FornecedorAdminDetail] erro ao fazer downgrade', err);
        this.notify.showError('Erro ao fazer downgrade');
      }
    });
  }

  openSubscriptionModal() {
    if (!this.fornecedor) return;
    if (!this.isVitrine(this.fornecedor)) {
      this.notify.showInfo('Edição de assinatura disponível apenas para plano Vitrine');
      return;
    }

    const planLevel = this.isVitrine(this.fornecedor) ? 'Vitrine' : 'Free';
    const paymentStatus = this.getNormalizedPaymentStatus(this.fornecedor);

    const expiresRaw = this.getSubscriptionExpiryDate(this.fornecedor);
    const expiresAt = expiresRaw ? new Date(expiresRaw).toISOString().slice(0, 10) : '';

    const vitrineSinceRaw = this.getVitrineSinceDate(this.fornecedor);
    const vitrineSinceDate = vitrineSinceRaw ? new Date(vitrineSinceRaw).toISOString().slice(0, 10) : '';
    const valorMensal = this.fornecedor?.valorMensal ?? this.fornecedor?.valor ?? null;

    this.subscriptionForm.reset({
      planLevel,
      paymentStatus,
      expiresAt,
      vitrineSinceDate,
      valorMensal,
      reason: 'Ajuste manual via admin'
    });

    this.showSubscriptionModal = true;
  }

  closeSubscriptionModal() {
    this.showSubscriptionModal = false;
  }

  submitSubscriptionEdit() {
    if (this.subscriptionForm.invalid || this.savingSubscription || !this.fornecedorId) return;
    if (!this.fornecedor || !this.isVitrine(this.fornecedor)) {
      this.notify.showError('Apenas fornecedores do plano Vitrine podem editar assinatura');
      return;
    }

    const value = this.subscriptionForm.value;
    const vitrineSinceDate = value.vitrineSinceDate || null;
    const subscriptionExpiryDate = value.expiresAt || null;
    const valorMensal = value.valorMensal !== null && value.valorMensal !== undefined && value.valorMensal !== ''
      ? Number(value.valorMensal)
      : undefined;

    if (!vitrineSinceDate || !subscriptionExpiryDate) {
      this.notify.showError('Informe as datas de início e expiração da assinatura');
      return;
    }

    if (new Date(subscriptionExpiryDate) <= new Date(vitrineSinceDate)) {
      this.notify.showError('A data de expiração deve ser maior que a data de início');
      return;
    }

    const currentExpires = this.getSubscriptionExpiryDate(this.fornecedor);
    const currentVitrineSince = this.getVitrineSinceDate(this.fornecedor);
    const currentValorMensal = this.fornecedor?.valorMensal ?? this.fornecedor?.valor;

    const noChanges = (currentVitrineSince ? new Date(currentVitrineSince).toISOString().slice(0, 10) : null) === vitrineSinceDate
      && (currentExpires ? new Date(currentExpires).toISOString().slice(0, 10) : null) === subscriptionExpiryDate
      && (valorMensal === undefined || Number(currentValorMensal ?? 0) === valorMensal);

    if (noChanges) {
      this.notify.showInfo('Nenhuma alteração para salvar');
      return;
    }

    this.savingSubscription = true;
    this.svc.editSubscriptionDates(this.fornecedorId, {
      vitrineSinceDate,
      subscriptionExpiryDate,
      valorMensal,
      reason: value.reason || 'Ajuste manual via admin'
    }).subscribe({
        next: () => {
          this.savingSubscription = false;
          this.closeSubscriptionModal();
          this.notify.showSuccess('Dados de assinatura atualizados com sucesso');
          this.load();
        },
        error: (err) => {
          this.savingSubscription = false;
          console.error('[FornecedorAdminDetail] erro ao atualizar assinatura manualmente', err);
          this.notify.showError('Erro ao atualizar assinatura');
        }
      });
  }

  goBack() {
    this.router.navigate(['/admin/fornecedores']);
  }

  // Helpers
  getPlanLabel(f: any): string {
    const p = f?.planLevelText || f?.planLevel;
    if (p === 'Vitrine' || p === 1) return '⭐ Vitrine';
    return '📦 Free';
  }

  getStatusClass(status: string): string {
    if (status === 'Ativo') return 'bg-green-100 text-green-800';
    if (status === 'Zumbi') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  }

  isVitrine(f: any): boolean {
    const planText = String(f?.planLevelText || f?.planLevel || '').toLowerCase();
    return planText.includes('vitrine') || planText === '1';
  }

  getNormalizedPaymentStatus(f: any): 'active' | 'pending' | 'overdue' {
    const raw = String(
      f?.paymentStatus
      || f?.subscription?.status
      || f?.subscriptionStatus
      || f?.statusPagamento
      || ''
    ).toLowerCase();

    if (!raw) return 'active';
    if (raw.includes('pending') || raw.includes('pend')) return 'pending';
    if (raw.includes('completed') || raw.includes('paid') || raw.includes('active') || raw.includes('ativa')) return 'active';
    if (raw.includes('overdue') || raw.includes('expired') || raw.includes('venc')) return 'overdue';
    return 'overdue';
  }

  getPaymentStatusClass(f: any): string {
    const status = this.getNormalizedPaymentStatus(f);
    if (status === 'active') return 'bg-green-100 text-green-800';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  }

  getPaymentStatusLabel(f: any): string {
    const status = this.getNormalizedPaymentStatus(f);
    if (status === 'active') return '✅ Ativa';
    if (status === 'pending') return '⏸️ Pendente';
    return '⏳ Expirada';
  }

  getSubscriptionExpiryDate(f: any): string | undefined {
    return f?.subscriptionExpiresAt
      || f?.proximoVencimento
      || f?.nextDueDate
      || f?.subscription?.expiresAt
      || f?.subscription?.nextDueDate
      || f?.subscriptionExpiryDate
      || undefined;
  }

  getAutoRenew(f: any): boolean | undefined {
    const value = f?.autoRenew ?? f?.subscription?.autoRenew;
    if (value === undefined || value === null) return undefined;
    return !!value;
  }

  getPaymentMethod(f: any): string | undefined {
    return f?.paymentMethod || f?.subscription?.paymentMethod || undefined;
  }

  getPaymentMethodLabel(method?: string): string {
    if (!method) return '—';
    const normalized = String(method).toLowerCase();
    if (normalized.includes('credit')) return '💳 Cartão';
    if (normalized.includes('boleto')) return '📄 Boleto';
    if (normalized.includes('pix')) return '📱 PIX';
    return method;
  }

  getVitrineSinceDate(f: any): string | undefined {
    return f?.vitrineSinceDate || f?.vitrineSince || f?.VitrineSinceDate || undefined;
  }

  getAcceptanceRate(f: any): number {
    const stats = f?.stats;
    if (!stats) return 0;
    const total = stats.totalLeads || stats.acceptedLeads + stats.rejectedLeads || 0;
    if (!total) return 0;
    return Math.round(((stats.acceptedLeads || 0) / total) * 100);
  }

  leadsDisplayEnd(): number {
    return Math.min(this.leadsPage * this.leadsPageSize, this.leadsTotal);
  }

  onLeadsPageChange(p: number) {
    this.leadsPage = p;
    this.loadLeads();
  }
}
